import { useState, useEffect } from 'react'
import { supabase, supabaseRPC } from '../lib/supabase'

export interface UserSettings {
  gemini_api_key?: string;
}

interface UseUserSettingsReturn {
  settings: UserSettings | null;
  loading: boolean;
  error: string | null;
  saveSettings: (settings: UserSettings) => Promise<void>;
  loadSettings: () => Promise<void>;
  validateGeminiKey: (key: string) => Promise<{ ok: boolean; message?: string }>;
  clearGeminiKey: () => Promise<void>;
}

export function useUserSettings(): UseUserSettingsReturn {
  const [settings, setSettings] = useState<UserSettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      setError(null)

      const currentUserId = await supabaseRPC.getCurrentUserId()
      if (!currentUserId) {
        throw new Error('Usuário não autenticado')
      }

      const { data, error: dbError } = await supabase
        .from('users')
        .select('gemini_api_key')
        .eq('id', currentUserId)
        .maybeSingle()

      if (dbError) throw dbError

      setSettings({
        gemini_api_key: data?.gemini_api_key || undefined
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar configurações')
      console.error('Erro ao carregar configurações:', err)
    } finally {
      setLoading(false)
    }
  }

  const saveSettings = async (newSettings: UserSettings) => {
    try {
      setLoading(true)
      setError(null)

      const currentUserId = await supabaseRPC.getCurrentUserId()
      if (!currentUserId) {
        throw new Error('Usuário não autenticado')
      }

      try {
        await supabaseRPC.setGeminiApiKey(newSettings.gemini_api_key || null)
      } catch (rpcErr) {
        const { error: dbError } = await supabase
          .from('users')
          .update({
            gemini_api_key: (newSettings.gemini_api_key || null),
            updated_at: new Date().toISOString()
          })
          .eq('id', currentUserId)
        if (dbError) throw dbError
      }

      setSettings(newSettings)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar configurações')
      console.error('Erro ao salvar configurações:', err)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const clearGeminiKey = async () => {
    try {
      setLoading(true)
      setError(null)

      const currentUserId = await supabaseRPC.getCurrentUserId()
      if (!currentUserId) {
        throw new Error('Usuário não autenticado')
      }

      try {
        await supabaseRPC.setGeminiApiKey(null)
      } catch (rpcErr) {
        const { error: dbError } = await supabase
          .from('users')
          .update({
            gemini_api_key: null,
            updated_at: new Date().toISOString()
          })
          .eq('id', currentUserId)
        if (dbError) throw dbError
      }

      setSettings({ gemini_api_key: undefined })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao limpar chave')
      console.error('Erro ao limpar chave:', err)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const validateGeminiKey = async (key: string) => {
    const fail = (message?: string) => ({ ok: false, message })
    const pass = (message?: string) => ({ ok: true, message })
    try {
      const trimmed = (key || '').trim()
      if (!trimmed) return fail('Informe a chave da API ou clique em Limpar')

      const looksLikeGoogleKey = /^AIza[0-9A-Za-z_\-]{35}$/.test(trimmed)
      if (!looksLikeGoogleKey) {
        // Não impedir; apenas informar que o formato é incomum
        // Continuar validação remota
      }

      // Tentativa 1: listar modelos (GET) — sucesso indica chave válida
      try {
        const resModels = await fetch(`https://generativelanguage.googleapis.com/v1/models?key=${encodeURIComponent(trimmed)}`, {
          method: 'GET',
          headers: { 'x-goog-api-key': trimmed }
        })
        if (resModels.ok) return pass()
        const err = await safeJson(resModels)
        const msg = extractErrorMessage(err)
        if (resModels.status === 403 && /referer|referrer|domain|restriction|restricted/i.test(msg)) {
          // Chave válida porém com restrição de domínio → considerar válida para salvar
          return pass('Chave válida, mas com restrição de domínio; validação no navegador pode falhar.')
        }
      } catch (e) {
        // Ignorar e tentar próximos endpoints
      }

      // Tentativa 2: countTokens (POST)
      try {
        const controller = new AbortController()
        const t = setTimeout(() => controller.abort(), 8000)
        const geminiModel = import.meta.env.VITE_GEMINI_MODEL || 'gemini-1.5-flash';
        const res = await fetch(`https://generativelanguage.googleapis.com/v1/models/${geminiModel}:countTokens?key=${encodeURIComponent(trimmed)}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'x-goog-api-key': trimmed },
            body: JSON.stringify({ contents: [{ role: 'user', parts: [{ text: 'ping' }] }] }),
            signal: controller.signal
          }
        )
        clearTimeout(t)
        if (res.ok) {
          const json = await res.json()
          const ok = typeof json.totalTokens === 'number' || typeof json.total_tokens === 'number' || (Array.isArray(json.tokens) && json.tokens.length > 0)
          return ok ? pass() : fail('Resposta inesperada do serviço')
        }
        const err = await safeJson(res)
        const msg = extractErrorMessage(err)
        if (res.status === 403 && /referer|referrer|domain|restriction|restricted/i.test(msg)) {
          return pass('Chave válida, mas com restrição de domínio; validação no navegador pode falhar.')
        }
      } catch (e) {
        // Ignorar e tentar generateContent
      }

      // Tentativa 3: generateContent (POST)
      try {
        const geminiModel = import.meta.env.VITE_GEMINI_MODEL || 'gemini-1.5-flash';
        const resAlt = await fetch(`https://generativelanguage.googleapis.com/v1/models/${geminiModel}:generateContent?key=${encodeURIComponent(trimmed)}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'x-goog-api-key': trimmed },
            body: JSON.stringify({ contents: [{ role: 'user', parts: [{ text: 'ping' }] }] })
          }
        )
        if (resAlt.ok) {
          const altJson = await resAlt.json()
          return (!!altJson?.candidates && Array.isArray(altJson.candidates)) ? pass() : fail('Resposta inesperada do serviço')
        }
        const err = await safeJson(resAlt)
        const msg = extractErrorMessage(err)
        if (resAlt.status === 403 && /referer|referrer|domain|restriction|restricted/i.test(msg)) {
          return pass('Chave válida, mas com restrição de domínio; validação no navegador pode falhar.')
        }
        return fail(msg || 'Chave inválida ou sem permissão')
      } catch (e) {
        return fail('Falha de rede ao validar chave')
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Erro desconhecido'
      return fail(msg)
    }
  }

  async function safeJson(res: Response): Promise<any> {
    try { return await res.json() } catch { return {} }
  }

  function extractErrorMessage(err: any): string {
    const rawMessage = (err && (err.error?.message || err.message || err.error?.status || '')) || ''

    // Traduzir mensagens comuns da API do Google Gemini
    const translations: { [key: string]: string } = {
      'API key not valid. Please pass a valid API key.': 'Chave da API inválida. Por favor, forneça uma chave válida.',
      'API_KEY_INVALID': 'Chave da API inválida.',
      'PERMISSION_DENIED': 'Permissão negada. Verifique se a chave da API tem as permissões necessárias.',
      'QUOTA_EXCEEDED': 'Cota excedida. Você atingiu o limite de uso da API.',
      'RATE_LIMIT_EXCEEDED': 'Limite de taxa excedido. Aguarde um pouco antes de tentar novamente.',
      'INVALID_ARGUMENT': 'Argumento inválido. Verifique os dados enviados.',
      'RESOURCE_EXHAUSTED': 'Recurso esgotado. Tente novamente mais tarde.'
    }

    // Verificar se há uma tradução para a mensagem exata
    if (translations[rawMessage]) {
      return translations[rawMessage]
    }

    // Verificar se a mensagem contém alguma das chaves de tradução
    for (const [english, portuguese] of Object.entries(translations)) {
      if (rawMessage.includes(english)) {
        return portuguese
      }
    }

    return rawMessage
  }

  return {
    settings,
    loading,
    error,
    saveSettings,
    loadSettings,
    validateGeminiKey,
    clearGeminiKey
  }
}
